import { MemoizedSelector, createSelector } from '@ngrx/store';
import { StateUtils } from '@spartacus/core';
import { EntriesState } from '../../../../../core/features/store/base-feature-state';
import { AccountingCode, Cart, CartOwnerDetails, CartType, OrderEntry } from '../../../../../core/model';
import { PyStateUtils } from '../../../../../core/state';
import { entityProcessesLoaderStateSelector } from '../../../../../core/state/utils/entity-processes-loader';
import { CartState, StateWithCart } from '../cart-state';
import { getCartState } from './feature.selector';

export const getCartsState: MemoizedSelector<StateWithCart, StateUtils.EntityProcessesLoaderState<Cart>> = createSelector(
  getCartState,
  (state: CartState) => state.carts
);

export const getCartEntityFactory = (cartId: string): MemoizedSelector<StateWithCart, StateUtils.ProcessesLoaderState<Cart>> => {
  return createSelector(getCartsState, (state: StateUtils.EntityProcessesLoaderState<Cart>) =>
    entityProcessesLoaderStateSelector(state, cartId)
  );
};

export const getCartFactory = (cartId: string): MemoizedSelector<StateWithCart, Cart> => {
  return createSelector(getCartsState, (state: StateUtils.EntityProcessesLoaderState<Cart>) =>
    StateUtils.entityValueSelector(state, cartId)
  );
};

export const getCartIsStableFactory = (cartId: string): MemoizedSelector<StateWithCart, boolean> => {
  return createSelector(getCartsState, (state: StateUtils.EntityProcessesLoaderState<Cart>) =>
    PyStateUtils.entityIsStableSelector(state, cartId)
  );
};

export const getCartHasPendingProcessesFactory = (cartId: string): MemoizedSelector<StateWithCart, boolean> => {
  return createSelector(getCartsState, (state: StateUtils.EntityProcessesLoaderState<Cart>) =>
    PyStateUtils.entityHasPendingProcessesSelector(state, cartId)
  );
};

export const getCartEntriesFactory = (cartId: string): MemoizedSelector<StateWithCart, OrderEntry[]> => {
  return createSelector(getCartFactory(cartId), (state: Cart) => {
    return state && state.entries ? state.entries : [];
  });
};

export const getCartEntryByIdFactory = (cartId: string, entryId: string): MemoizedSelector<StateWithCart, OrderEntry> => {
  return createSelector(getCartEntriesFactory(cartId), (state: OrderEntry[]) => {
    return state ? state.find((entry) => entry.id === entryId) : undefined;
  });
};

export const getCartEntryAccountingCodeFactory = (
  cartId: string,
  entry: OrderEntry
): MemoizedSelector<StateWithCart, AccountingCode> => {
  return createSelector(getCartEntryByIdFactory(cartId, entry.id), (state: OrderEntry) => {
    return state ? state.accountingCode : undefined;
  });
};

export const getActiveCartIds: MemoizedSelector<StateWithCart, EntriesState<string>> = createSelector(
  getCartState,
  (state: CartState) => state.active
);

export const getActiveCartIdFactory = (cartType: CartType): MemoizedSelector<StateWithCart, string | undefined> => {
  return createSelector(getActiveCartIds, (state: EntriesState<string>) => {
    return state && state[cartType];
  });
};

export const getCartOwnerDetails = (cartId: string): MemoizedSelector<StateWithCart, CartOwnerDetails> => {
  return createSelector(getCartFactory(cartId), (state: Cart) => {
    return state?.cartOwnerDetails || undefined;
  });
};
